import { useState, useEffect } from "react";
import { api, errMsg, fmtPrice } from "../../lib/api";
import { Trash2, Pencil, Package } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", description: "", course_ids: [], price: "", published: true };

export default function AdminBundles() {
  const [bundles, setBundles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/admin/bundles").then((r) => setBundles(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const toggleCourse = (id) => setForm((f) => ({
    ...f, course_ids: f.course_ids.includes(id) ? f.course_ids.filter((x) => x !== id) : [...f.course_ids, id],
  }));

  const save = async () => {
    try {
      const body = { ...form, price: parseFloat(form.price) };
      if (editId) await api.put(`/admin/bundles/${editId}`, body);
      else await api.post("/admin/bundles", body);
      toast.success(editId ? "Bundle updated ✓" : "Bundle created ✓");
      setForm(null); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bundle?")) return;
    await api.delete(`/admin/bundles/${id}`);
    toast.success("Bundle deleted"); load();
  };

  const togglePublish = async (b) => {
    await api.put(`/admin/bundles/${b.id}`, { title: b.title, description: b.description || "",
      course_ids: b.course_ids, price: b.price, published: !b.published });
    load();
  };

  const selValue = form ? form.course_ids.reduce((n, id) => n + (courses.find((c) => c.id === id)?.effective_price || 0), 0) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-xl" data-testid="admin-bundles-title">COURSE BUNDLES</h1>
        <button className="btn-primary !py-2 text-sm" data-testid="new-bundle-btn"
          onClick={() => { setForm({ ...empty }); setEditId(null); }}>+ NEW BUNDLE</button>
      </div>
      {form && (
        <div className="forge-card !transform-none p-4 mb-6" data-testid="bundle-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Title</label>
              <input data-testid="bundle-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Blueprint Master Pack" className="input-dark text-sm mt-1" /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Bundle price (USD)</label>
              <input data-testid="bundle-price-input" type="number" min="1" step="0.01" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="49.99" className="input-dark text-sm mt-1" /></div>
          </div>
          <div className="mt-3"><label className="text-xs" style={{ color: "var(--muted)" }}>Description (optional)</label>
            <input data-testid="bundle-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Everything you need to go from zero to Blueprint pro" className="input-dark text-sm mt-1" /></div>
          <label className="text-xs block mt-3 mb-1" style={{ color: "var(--muted)" }}>Courses in bundle (pick 2+)</label>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {courses.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm p-2 rounded border cursor-pointer"
                style={{ borderColor: form.course_ids.includes(c.id) ? "var(--bp)" : "var(--line)" }}
                data-testid={`bundle-course-check-${c.id}`}>
                <input type="checkbox" checked={form.course_ids.includes(c.id)} onChange={() => toggleCourse(c.id)} />
                <span className="flex-1 truncate">{c.title}</span>
                <span style={{ color: "var(--muted)" }}>${c.effective_price}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button className="btn-primary !py-2 text-sm" data-testid="bundle-save-btn" onClick={save}
              disabled={!form.title.trim() || form.course_ids.length < 2 || !form.price}>SAVE BUNDLE</button>
            <button className="btn-ghost !py-2 text-sm" onClick={() => { setForm(null); setEditId(null); }}>Cancel</button>
            {form.course_ids.length >= 2 && form.price && (
              <span className="text-xs" style={{ color: "var(--gold)" }}>
                Value ${selValue.toFixed(2)} → ${parseFloat(form.price || 0).toFixed(2)} ({Math.max(0, Math.round((1 - form.price / selValue) * 100))}% off)
              </span>
            )}
          </div>
        </div>
      )}
      {bundles.length === 0 && !form && <div className="empty" data-testid="bundles-empty">No bundles yet — create your first one to boost order value.</div>}
      <div className="space-y-3">
        {bundles.map((b) => (
          <div key={b.id} className="forge-card !transform-none p-4 flex items-center gap-4" data-testid={`admin-bundle-${b.id}`}>
            <Package size={20} style={{ color: "var(--gold)" }} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{b.title}</p>
              <p className="text-xs truncate" style={{ color: "var(--muted)" }}>{b.courses.map((c) => c.title).join(" + ")}</p>
            </div>
            <span className="text-sm font-display" style={{ color: "var(--land)" }}>{fmtPrice(b.price, "USD")}</span>
            <span className="text-xs line-through" style={{ color: "var(--muted)" }}>{fmtPrice(b.total_value, "USD")}</span>
            <span className="text-xs font-bold" style={{ color: "var(--gold)" }}>−{b.savings_pct}%</span>
            <button data-testid={`bundle-publish-toggle-${b.id}`} onClick={() => togglePublish(b)}
              className="text-xs font-display px-2.5 py-1 rounded border"
              style={b.published ? { color: "var(--land)", borderColor: "rgba(82,210,115,.4)" } : { color: "var(--muted)", borderColor: "var(--line)" }}>
              {b.published ? "LIVE" : "HIDDEN"}
            </button>
            <button data-testid={`bundle-edit-${b.id}`} onClick={() => { setForm({ title: b.title, description: b.description || "", course_ids: b.course_ids, price: String(b.price), published: b.published }); setEditId(b.id); }}
              className="p-1.5 hover:text-white" style={{ color: "var(--muted)" }}><Pencil size={14} /></button>
            <button data-testid={`bundle-delete-${b.id}`} onClick={() => remove(b.id)}
              className="p-1.5 hover:text-red-400" style={{ color: "var(--muted)" }}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
